import BlockTitle from '@src/components/_shared/UI/BlockTitle';
import Container from '@src/components/_shared/Container';
import Subtitle from '@src/components/_shared/UI/Subtitle';
import styles from './UploadMissingDocs.module.scss';
import useRegistrationStore from '@src/stores/registrationStore';
import { useMutation, useQuery } from '@tanstack/react-query';
import { checkIbanStatus, companyFileUpdate, companyFileUpload, getCompanyDocumentsStatus } from '@src/api/Registration';
import GreenCheck from '@src/assets/GreenCheck.png';
import { useState, useEffect } from 'react';
import { CompanyDocumentsType, DocumentLabelType, EdealDocument } from '@src/utilities/types';
import { getCompanyDocs } from '../../SixthSection/CompanyDocuments/companyDocsConfig';
import FileUploadInput from '@src/components/_shared/UI/FileUploadInput';
import { MAXSIZEFILE } from '@src/utilities/constants';
import toast from 'react-hot-toast';
import Loader from '@src/components/_shared/UI/Loader';
import { handleApiError } from '@src/utilities/functions';
import ValidateButton from '@src/components/_shared/UI/ValidateButton';

export default function UploadMissingDocs() {
    const { registration } = useRegistrationStore();

    const { data: ibanData, isLoading: ibanLoading } = useQuery({
        queryKey: [`getIbandStatus`],
        queryFn: () => checkIbanStatus({iban: registration.company.ibans[0].iban, companyId: registration.company.company_id}),
    });
    const { data: companyDocumentsStatus, refetch, isRefetching, isLoading } = useQuery({
        queryKey: [`getCompanyDocumentsStatus`],
        queryFn: () => getCompanyDocumentsStatus(registration.partner_informations.is_partner ? registration.person_id : undefined),
    });

    const [docsDataState, setDocsDataState] = useState<CompanyDocumentsType[]>([]);
    const [uploadingFileFrontkey, setUploadingFileFrontkey] = useState<string | null>(null);
    const [lastUpdatedFrontKey, setLastUpdatedFrontKey] = useState<string | null>(null);
    const [hasCompletedUpload, setHasCompletedUpload] = useState<boolean>(false);

    useEffect(() => {
        const requiredDocs = getCompanyDocs(registration, ibanData);
        const existingDocs = companyDocumentsStatus?.data?.documents;
        if (existingDocs && Array.isArray(existingDocs)) {
            const docsToDisplay = requiredDocs
                .map((doc: CompanyDocumentsType) => {
                    // Cas particulier tax-package / 2 documents ont les mêmes type / type_detail
                    const taxPackageDocs = existingDocs.filter(
                        (existingDoc: EdealDocument) => existingDoc.type_detail?.name === 'tax-package'
                    );
                    if (doc.frontKey === 'financialStatements' && taxPackageDocs.length > 0) {
                        const updatedDoc = taxPackageDocs[0];
                        return {
                            ...doc,
                            id: updatedDoc.id,
                            filename: updatedDoc.filename,
                            size: updatedDoc.size,
                            status: updatedDoc.status
                        };
                    }
                    if (doc.frontKey === 'financialStatementsN-1' && taxPackageDocs.length > 1) {
                        const updatedDoc = taxPackageDocs[1];
                        return {
                            ...doc,
                            id: updatedDoc.id,
                            filename: updatedDoc.filename,
                            size: updatedDoc.size,
                            status: updatedDoc.status
                        };
                    }
                    if (doc.type_detail !== 'tax-package') {
                        const updatedDoc = existingDocs.find(
                            (existingDoc: EdealDocument) => 
                                existingDoc.type?.name === doc.type && 
                                existingDoc.type_detail?.name === doc.type_detail
                        );
                        return updatedDoc
                            ? { ...doc, id: updatedDoc.id, filename: updatedDoc.filename, size: updatedDoc.size, status: updatedDoc.status }
                            : doc;
                    }
                    return doc;
                })
                .filter((doc: CompanyDocumentsType) => {
                    return !doc.status || doc.status === 'Refusé';
                });
            setDocsDataState(docsToDisplay);
        } else {
            setDocsDataState(requiredDocs);
        }
    }, [registration, ibanData, companyDocumentsStatus]);

    const { mutate, isPending } = useMutation({
        mutationFn: (formData: FormData) => companyFileUpload(formData),
        onSuccess: () => {
            toast.success('Le fichier a été téléchargé avec succès !', { position: 'top-center' });
            setUploadingFileFrontkey(null);
            refetch();
        },
        onError: (error) => {
            handleApiError(error, 'Une erreur est survenue lors du téléchargement du fichier.');
            setUploadingFileFrontkey(null);
            setLastUpdatedFrontKey(null);
        }
    })

    const { mutate: mutateUpdate, isPending: isPendingUpdate } = useMutation({
        mutationFn: ({formData, id}: {formData: FormData, id: string}) => companyFileUpdate(formData, id),
        onSuccess: () => {
            toast.success('Le fichier a été téléchargé avec succès !', { position: 'top-center' });
            setUploadingFileFrontkey(null);
            refetch();
        },
        onError: (error) => {
            handleApiError(error, 'Une erreur est survenue lors du téléchargement du fichier.');
            setUploadingFileFrontkey(null);
            setLastUpdatedFrontKey(null);
        }
    })


    const _handleSelectFile = async (frontKey: string, file: File, type: string, type_detail: string, id?: string) => {
        const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg'];
        if (!allowedTypes.includes(file.type)) {
            return toast.error('Format de fichier non supporté. Veuillez choisir un fichier PDF ou une image (.png, .jpeg).', { position: 'top-center' });
        }
        if (file.size > MAXSIZEFILE) {
            return toast.error('Ce fichier est trop volumineux. La taille maximum est de 10 Mo.', { position: 'top-center'});
        }
        const companyFile = {
            frontKey: frontKey,
            fileName: file.name,
            fileSize: file.size,
            file: file,
            type: type,
            type_detail: type_detail,
            label: file.type === 'application/pdf' ? DocumentLabelType.pdf : DocumentLabelType.image,
        }
        if (frontKey === 'collectiveDecision') {
            toast.error('Attention : Vous devez également joindre les documents d\'identités des associés dans "Autre pièce"', { position: 'top-center' });
        }
        if (companyFile) {
            const formData = new FormData();
            formData.append('file', companyFile.file);
            formData.append('demand_id', registration.demand_id);
            formData.append('type', companyFile.type);
            formData.append('type_detail', companyFile.type_detail);
            if (registration.partner_informations.is_partner) formData.append('is_psi', 'true');
            if (registration.person_id) formData.append('person_id', registration.person_id);
            setUploadingFileFrontkey(frontKey);
            setLastUpdatedFrontKey(frontKey);

            if (id) {
                mutateUpdate({formData, id});
            } else {
                mutate(formData);
            }
        } else {
            toast.error('erreur', { position: 'top-center' });
        }
    };

    if (ibanLoading || isLoading) {
        return (
            <Container>
                <div style={{marginTop: '200px'}}>
                    <Loader type="SPINNER" />
                </div>
            </Container>
        );
    }

    if (hasCompletedUpload) {
        return (
            <Container>
                <img src={GreenCheck} alt="Close" width={155} height={149} style={{margin: '80px auto'}} />
                <BlockTitle title="Nous vous remercions pour l’envoi des documents nécessaires à la finalisation de votre dossier !" />
                <Subtitle content="Votre demande d’inscription sera effective lorsque le ou les documents seront validés par nos services." />
            </Container>
        )
    }

    return (
        <Container>
            <div style={{marginTop: '50px'}} />
            <BlockTitle title="Transférez les documents manquants" />
            <Subtitle content="Télécharger les pièces justificatives suivantes" />
            <p className={styles.content}>Fichiers acceptés : .pdf, images (.jpeg, .png)</p>
            {docsDataState.map(({ id, frontKey, filename, size, type, type_detail, title, status }: CompanyDocumentsType) => (
                <FileUploadInput
                    key={frontKey}
                    title={title}
                    documentName={filename}
                    initialFileSize={size ?? 0}
                    isValidated={!!filename}
                    selectFile={(file) => _handleSelectFile(frontKey, file, type, type_detail, id)}
                    isLoading={uploadingFileFrontkey === frontKey || (isRefetching && lastUpdatedFrontKey === frontKey)}
                    accept=".pdf,image/png,image/jpeg"
                    isApproved={status === 'valide'}
                    isCompanyFile
                    hasError={status === 'Refusé'}
                    errorMessage={status === 'Refusé' ? 'Le document a été refusé, veuillez en joindre un nouveau' : ''}
                />
            ))}
            <FileUploadInput
                key="extraFile"
                title="Autre pièce"
                selectFile={(file) => _handleSelectFile("extraFile", file, "status-acts", "others")}
                isLoading={uploadingFileFrontkey === "extraFile" || (isRefetching && lastUpdatedFrontKey === "extraFile")}
                accept=".pdf,image/png,image/jpeg"
            />
            <ValidateButton title="Terminer" onClick={() => setHasCompletedUpload(true)} disabled={isPending || isPendingUpdate} onLoading={isPending || isPendingUpdate} />
        </Container>
    );
}
